import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CornerDownRight, Pencil, Send, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  Drawer,
  ErrorState,
  Field,
  InlineAlert,
  Input,
  LoadingState,
  StatusBadge,
  Textarea,
  cn,
} from "../../shared/ui";
import type {
  CommentView,
  HistoryView,
  ScheduleChangeView,
  WorklogView,
} from "../../shared/types/api";
import {
  APPROVAL_LABELS,
  APPROVAL_TONE,
  CHANGE_TYPE_LABELS,
  REASON_LABELS,
  formatDays,
} from "../change/changeVocabulary";
import { formatMinutes } from "../timesheets/timesheetVocabulary";
import { MentionBox, mentionsStillIn } from "./MentionBox";
import {
  formatTimestamp,
  historyValue,
  phraseHistoryEntry,
  showsValues,
} from "./workItemVocabulary";

type Tab = "all" | "comments" | "changes" | "worklogs" | "schedule";

const TABS: { key: Tab; label: string; phase?: string }[] = [
  { key: "comments", label: "Comments" },
  { key: "all", label: "All" },
  { key: "changes", label: "Changes" },
  { key: "worklogs", label: "Worklogs" },
  { key: "schedule", label: "Schedule changes" },
];

/**
 * The §63 activity panel.
 *
 * <p>Five tabs because the entries are read for five different reasons: everything in order, what
 * people said, what people changed, what people spent, and which dates moved. A single merged
 * feed is unreadable on a busy item — the one comment somebody is looking for ends up buried
 * under forty status changes.
 *
 * <p>The Worklogs tab answers the question the estimate cannot: where the hours actually went,
 * and whose they were. §29 keeps the item's total in step with these rows, so this is the
 * breakdown behind the "actual" figure shown at the top of the item.
 */
export function WorkItemActivity({ workItemId }: { workItemId: string }) {
  /*
   * Comments first, and first in the row of tabs. What somebody opening an item wants is the
   * conversation — the questions asked and the answers given. Everything in order is the wider
   * question, and on a busy item the one comment they came for arrives under forty status
   * changes, which is the merged feed's whole weakness.
   */
  const [tab, setTab] = useState<Tab>("comments");

  const history = useQuery({
    queryKey: ["work-item-history", workItemId],
    queryFn: () =>
      api.get<HistoryView[]>(`/api/v1/work-items/${workItemId}/history`),
  });

  const comments = useQuery({
    queryKey: ["work-item-comments", workItemId],
    queryFn: () =>
      api.get<CommentView[]>(`/api/v1/work-items/${workItemId}/comments`),
  });

  const reload = () => {
    void history.refetch();
    void comments.refetch();
  };

  return (
    <Card>
      <div className="flex gap-1 border-b border-ink-200 px-5">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setTab(entry.key)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
              tab === entry.key
                ? "border-brand-600 text-brand-700"
                : "border-transparent text-ink-500 hover:text-ink-800",
            )}
          >
            {entry.label}
            {entry.phase && (
              <span className="ml-1.5 rounded bg-ink-100 px-1 text-[10px] text-ink-500">
                {entry.phase.replace("Phase ", "P")}
              </span>
            )}
          </button>
        ))}
      </div>

      <CardBody>
        {tab === "comments" ? (
          <Comments
            workItemId={workItemId}
            query={comments}
            onChanged={reload}
          />
        ) : tab === "worklogs" ? (
          <Worklogs workItemId={workItemId} />
        ) : tab === "schedule" ? (
          <ScheduleChanges workItemId={workItemId} />
        ) : (
          <History query={history} filter={tab} />
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Every date that has moved, and why (§58).
 *
 * <p>Not the history entries filtered down — those say a date changed, which is the smaller half.
 * §57's record carries the reason code, the impact and the sign-off, and that is what somebody
 * looking at a slipped item has come here to read.
 */
function ScheduleChanges({ workItemId }: { workItemId: string }) {
  const changes = useQuery({
    queryKey: ["work-item-schedule-changes", workItemId],
    queryFn: () =>
      api.get<ScheduleChangeView[]>(
        `/api/v1/work-items/${workItemId}/schedule-changes`,
      ),
  });

  if (changes.isPending) {
    return <LoadingState label="Loading schedule changes…" />;
  }
  if (changes.isError) {
    return (
      <ErrorState
        error={changes.error}
        onRetry={() => void changes.refetch()}
      />
    );
  }
  if (changes.data.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-ink-500">
        No dates have moved. Nothing is hidden here — this item is where it was
        first planned.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-ink-100">
      {changes.data.map((change) => (
        <li key={change.id} className="py-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm text-ink-800">
              <span className="font-medium">
                {CHANGE_TYPE_LABELS[change.changeType]}
              </span>{" "}
              <span className="tnum text-ink-500">
                {change.oldDate ?? "not set"} → {change.newDate}
              </span>
            </span>
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  "tnum text-sm",
                  change.slippageDays > 0
                    ? "text-amber-700"
                    : change.slippageDays < 0
                      ? "text-emerald-700"
                      : "text-ink-500",
                )}
              >
                {formatDays(change.slippageDays)}
              </span>
              {change.approvalRequired && (
                <StatusBadge tone={APPROVAL_TONE[change.approvalStatus]}>
                  {APPROVAL_LABELS[change.approvalStatus]}
                </StatusBadge>
              )}
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-700">
            {REASON_LABELS[change.reasonCode]}
            {change.reasonComment && (
              <span className="text-ink-500"> — {change.reasonComment}</span>
            )}
          </p>
          {change.impactDescription && (
            <p className="mt-0.5 text-xs text-ink-500">
              Impact: {change.impactDescription}
              {change.impactHours != null && ` (${change.impactHours}h)`}
            </p>
          )}
          {change.linkedBlockerTitle && (
            <p className="mt-0.5 text-xs text-ink-500">
              Caused by: {change.linkedBlockerTitle}
            </p>
          )}
          <p className="mt-1 text-xs text-ink-500">
            {change.changedByName} · {formatTimestamp(change.changedAt)}
            {change.approvedByName && ` · decided by ${change.approvedByName}`}
          </p>
          {change.approvalNote && (
            <p className="mt-0.5 text-xs text-ink-500">
              "{change.approvalNote}"
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Time logged against this item (§63), and the place to correct it.
 *
 * <p>New time is still entered on a timesheet, where the day it belongs to and the period it
 * falls in are both in view — logging four hours from here, with no sight of what else that day
 * already holds, is how somebody ends up with a fourteen-hour Tuesday.
 *
 * <p>Fixing an entry is the other way round. A wrong figure is noticed here, looking at the item
 * and its estimate, and sending somebody off to find the right day on the right timesheet to
 * change one number is how wrong figures stay. The rules do not change with the doorway: an
 * entry belonging to somebody else, or falling in a period that has been submitted, is shown and
 * not editable.
 */
function Worklogs({ workItemId }: { workItemId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<WorklogView | null>(null);

  const worklogs = useQuery({
    queryKey: ["work-item-worklogs", workItemId],
    queryFn: () =>
      api.get<WorklogView[]>(`/api/v1/work-items/${workItemId}/worklogs`),
  });

  /*
   * The item's logged total is a roll-up of these rows, recomputed server-side on every change,
   * so the item itself has to be re-read as well. Refreshing only this list would leave the
   * Effort card beside it showing the figure from before the edit.
   */
  const reloadAll = () => {
    void worklogs.refetch();
    void queryClient.invalidateQueries({ queryKey: ["work-item", workItemId] });
    void queryClient.invalidateQueries({
      queryKey: ["work-item-history", workItemId],
    });
  };

  if (worklogs.isPending) {
    return <LoadingState label="Loading worklogs…" />;
  }
  if (worklogs.isError) {
    return (
      <ErrorState
        error={worklogs.error}
        onRetry={() => void worklogs.refetch()}
      />
    );
  }
  if (worklogs.data.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-ink-400">
        No time logged against this item yet.
      </p>
    );
  }

  const total = worklogs.data.reduce((sum, entry) => sum + entry.minutes, 0);

  return (
    <div>
      <ul className="divide-y divide-ink-100">
        {worklogs.data.map((entry) => (
          <li
            key={entry.id}
            className="flex items-start justify-between gap-4 py-2.5"
          >
            <div className="min-w-0">
              <p className="text-sm text-ink-800">
                <span className="font-medium">{entry.employeeName}</span>
                <span className="text-ink-500"> · {entry.workDate}</span>
              </p>
              {entry.description && (
                <p className="mt-0.5 text-sm text-ink-500">
                  {entry.description}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="tnum text-sm font-medium text-ink-900">
                {formatMinutes(entry.minutes)}
              </span>
              {/* Offered only when it would actually work. An edit button on a submitted
                  period, or on somebody else's entry, is a button whose whole function is to
                  produce an error message. */}
              {entry.editable && can("TIMESHEET_CREATE") && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Pencil className="h-3.5 w-3.5" />}
                  onClick={() => setEditing(entry)}
                >
                  Edit
                </Button>
              )}
              {!entry.editable && (
                <span
                  className="text-xs text-ink-400"
                  title="The timesheet period this falls in has been submitted."
                >
                  locked
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex justify-between border-t border-ink-200 pt-2 text-sm">
        <span className="text-ink-500">Total logged</span>
        <span className="tnum font-semibold text-ink-900">
          {formatMinutes(total)}
        </span>
      </div>
      <p className="mt-2 text-xs text-ink-400">
        New time is entered on a timesheet, where the rest of that day is in
        view. Corrections belong here, where the figure is being read against
        the estimate.
      </p>

      <EditWorklogDrawer
        worklog={editing}
        onClose={() => setEditing(null)}
        onSaved={reloadAll}
      />
    </div>
  );
}

/**
 * Corrects one logged entry: its hours, its date, or what it was for.
 *
 * <p>Deleting is here too, and is a real answer — time logged against the wrong item is not
 * fixed by setting it to zero, which the database refuses anyway. The entry moves or goes.
 */
function EditWorklogDrawer({
  worklog,
  onClose,
  onSaved,
}: {
  worklog: WorklogView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [hours, setHours] = useState("");
  const [workDate, setWorkDate] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (worklog) {
      setHours(String(worklog.hours));
      setWorkDate(worklog.workDate);
      setDescription(worklog.description ?? "");
      setError(null);
    }
  }, [worklog]);

  const minutes = hours === "" ? 0 : Math.round(Number(hours) * 60);

  const submit = async () => {
    if (!worklog) {
      return;
    }
    if (minutes <= 0) {
      setError(
        "Give a figure above zero. To remove the entry entirely, delete it.",
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/api/v1/worklogs/${worklog.id}`, {
        minutes,
        description: description || null,
        workDate,
        version: worklog.version,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not save that entry.",
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!worklog) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.del(`/api/v1/worklogs/${worklog.id}`);
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not remove that entry.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(worklog)}
      onClose={onClose}
      title="Correct this entry"
      subtitle={
        worklog ? `${worklog.employeeName} · ${worklog.workDate}` : undefined
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => void remove()}
            disabled={saving}
          >
            Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} loading={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Hours" required>
            <Input
              type="number"
              min="0"
              step="0.25"
              value={hours}
              onChange={(event) => setHours(event.target.value)}
            />
          </Field>
          <Field
            label="Date"
            required
            hint="Moving it to a submitted period is refused."
          >
            <Input
              type="date"
              value={workDate}
              onChange={(event) => setWorkDate(event.target.value)}
            />
          </Field>
        </div>

        <Field label="What it was for">
          <Textarea
            rows={2}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Tracing where the second rounding happens"
          />
        </Field>

        <InlineAlert tone="info">
          The item's logged total is the sum of its entries, so it moves with
          this. Both the change and its old value stay on the item's history.
        </InlineAlert>
      </div>
    </Drawer>
  );
}

/** The timeline, filtered by what the reader came for. */
function History({
  query,
  filter,
}: {
  query: ReturnType<typeof useQuery<HistoryView[]>>;
  filter: Tab;
}) {
  if (query.isPending) {
    return <LoadingState label="Loading activity…" />;
  }
  if (query.isError) {
    return (
      <ErrorState error={query.error} onRetry={() => void query.refetch()} />
    );
  }

  // "schedule" never reaches here: since §58 that tab renders the change records themselves,
  // which carry the reason and the sign-off that a history entry cannot.
  const entries =
    filter === "changes"
      ? query.data.filter((entry) => entry.fieldChange)
      : query.data;

  if (entries.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-ink-500">
        Nothing recorded yet.
      </p>
    );
  }

  return (
    <ol className="space-y-3">
      {entries.map((entry) => (
        <li key={entry.id} className="flex gap-3">
          <span
            className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-ink-300"
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-ink-800">
              <span className="font-medium text-ink-900">
                {entry.actorName ?? "The system"}
              </span>{" "}
              {/* A named move reads as the act it was: "changed the status from DONE to
                  IN_PROGRESS" and "moved this — Reopen" are the same row, and only one of them
                  tells the reader what happened. */}
              {entry.transitionLabel != null ? (
                <>
                  moved this{" "}
                  <span className="rounded-md bg-brand-50 px-1.5 py-0.5 text-xs font-medium text-brand-700">
                    {entry.transitionLabel}
                  </span>
                </>
              ) : (
                phraseHistoryEntry(entry.eventType, entry.fieldName)
              )}
              {/* Some events are recorded without the values they changed, and saying so is the
                  whole entry. Appending the generic from/to clause anyway is what turned a
                  description edit into "from (previous text) to (updated)" — a line claiming the
                  description had been set to the word "(updated)". Rows written before the server
                  stopped storing those placeholders are still in the database, and history is
                  append-only, so the guard belongs here rather than only at the point of writing. */}
              {showsValues(entry.eventType) && (
                <>
                  {entry.oldValue != null && entry.newValue != null && (
                    <>
                      {" "}
                      <span className="text-ink-500">from</span>{" "}
                      <span className="tnum font-medium text-ink-700">
                        {historyValue(entry.eventType, entry.oldValue)}
                      </span>{" "}
                      <span className="text-ink-500">to</span>{" "}
                      <span className="tnum font-medium text-ink-900">
                        {historyValue(entry.eventType, entry.newValue)}
                      </span>
                    </>
                  )}
                  {entry.oldValue == null && entry.newValue != null && (
                    <>
                      {" "}
                      <span className="tnum font-medium text-ink-900">
                        {historyValue(entry.eventType, entry.newValue)}
                      </span>
                    </>
                  )}
                  {entry.oldValue != null && entry.newValue == null && (
                    <>
                      {" "}
                      <span className="text-ink-500">removed</span>{" "}
                      <span className="tnum text-ink-700">
                        {entry.oldValue}
                      </span>
                    </>
                  )}
                </>
              )}
            </p>
            {/* The reason is the whole point of asking for one; burying it behind a hover would
                make the record technically complete and practically unreadable.

                Shown for a written explanation as well as a coded one. Gating this on the label
                alone hid every re-estimate's reason: those carry a sentence and no code, so the
                words people were required to type were stored and never displayed again. */}
            {(entry.reasonLabel != null || entry.reasonComment != null) && (
              <p className="mt-1 rounded-md border-l-2 border-ink-200 bg-ink-50 px-2 py-1 text-xs text-ink-700">
                {entry.reasonLabel != null && (
                  <span className="font-medium">{entry.reasonLabel}</span>
                )}
                {entry.reasonLabel != null &&
                  entry.reasonComment != null &&
                  " — "}
                {entry.reasonComment}
              </p>
            )}
            <p className="mt-0.5 text-xs text-ink-400">
              {formatTimestamp(entry.eventTimestamp)}
              {entry.source !== "USER" && ` · ${entry.source.toLowerCase()}`}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** The conversation (§87). */
function Comments({
  workItemId,
  query,
  onChanged,
}: {
  workItemId: string;
  query: ReturnType<typeof useQuery<CommentView[]>>;
  onChanged: () => void;
}) {
  const { user, can } = useAuth();
  const [body, setBody] = useState("");
  /** Who the composer has named so far, as id → name. See MentionBox. */
  const [mentioned, setMentioned] = useState<Record<string, string>>({});
  const [replyTo, setReplyTo] = useState<CommentView | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (query.isPending) {
    return <LoadingState label="Loading comments…" />;
  }
  if (query.isError) {
    return (
      <ErrorState error={query.error} onRetry={() => void query.refetch()} />
    );
  }

  const comments = query.data;
  const topLevel = comments.filter(
    (comment) => comment.parentCommentId == null,
  );
  const repliesTo = (id: string) =>
    comments.filter((comment) => comment.parentCommentId === id);

  const post = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-items/${workItemId}/comments`, {
        body,
        parentCommentId: replyTo?.id ?? null,
        mentionedIds: mentionsStillIn(body, mentioned),
      });
      setBody("");
      setMentioned({});
      setReplyTo(null);
      onChanged();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Could not post that.",
      );
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (commentId: string) => {
    setError(null);
    try {
      await api.patch(`/api/v1/work-items/comments/${commentId}`, {
        body: editBody,
      });
      setEditingId(null);
      onChanged();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Could not save that.",
      );
    }
  };

  const remove = async (commentId: string) => {
    setError(null);
    try {
      await api.del(`/api/v1/work-items/comments/${commentId}`);
      onChanged();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not withdraw that.",
      );
    }
  };

  const renderComment = (comment: CommentView, isReply: boolean) => {
    const mine = comment.authorId === user?.employeeId;

    return (
      <div
        key={comment.id}
        className={cn(isReply && "ml-6 border-l border-ink-200 pl-4")}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-ink-900">
            {comment.authorName}
          </span>
          <span className="text-xs text-ink-400">
            {formatTimestamp(comment.createdAt)}
          </span>
          {comment.edited && !comment.deleted && (
            <span
              className="text-xs text-ink-400"
              title="This comment was changed after posting"
            >
              edited
            </span>
          )}
        </div>

        {editingId === comment.id ? (
          <div className="mt-1.5 space-y-2">
            <Textarea
              rows={3}
              value={editBody}
              onChange={(event) => setEditBody(event.target.value)}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void saveEdit(comment.id)}>
                Save
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditingId(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : comment.deleted ? (
          // The row stays so the replies underneath still make sense (§87).
          <p className="mt-0.5 text-sm italic text-ink-400">
            This comment was withdrawn.
          </p>
        ) : (
          <p className="mt-0.5 whitespace-pre-wrap text-sm text-ink-700">
            <WithMentions body={comment.body} names={comment.mentionedNames} />
          </p>
        )}

        {!comment.deleted && editingId !== comment.id && (
          <div className="mt-1 flex gap-1">
            {!isReply && can("COMMENT_CREATE") && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs text-ink-500"
                icon={<CornerDownRight className="h-3 w-3" />}
                onClick={() => setReplyTo(comment)}
              >
                Reply
              </Button>
            )}
            {mine && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 text-xs text-ink-500"
                  icon={<Pencil className="h-3 w-3" />}
                  onClick={() => {
                    setEditingId(comment.id);
                    setEditBody(comment.body ?? "");
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 text-xs text-ink-500 hover:text-red-600"
                  icon={<Trash2 className="h-3 w-3" />}
                  onClick={() => void remove(comment.id)}
                >
                  Withdraw
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {error && <InlineAlert tone="error">{error}</InlineAlert>}

      {topLevel.length === 0 ? (
        <p className="text-sm text-ink-500">Nothing said yet.</p>
      ) : (
        <div className="space-y-4">
          {topLevel.map((comment) => (
            <div key={comment.id} className="space-y-3">
              {renderComment(comment, false)}
              {repliesTo(comment.id).map((reply) => renderComment(reply, true))}
            </div>
          ))}
        </div>
      )}

      {can("COMMENT_CREATE") && (
        <div className="border-t border-ink-100 pt-4">
          {replyTo && (
            <p className="mb-1.5 flex items-center gap-2 text-xs text-ink-500">
              <CornerDownRight className="h-3 w-3" />
              Replying to {replyTo.authorName}
              <button
                type="button"
                className="text-brand-600 hover:underline"
                onClick={() => setReplyTo(null)}
              >
                cancel
              </button>
            </p>
          )}
          <MentionBox
            rows={3}
            value={body}
            onChange={setBody}
            mentioned={mentioned}
            onMentionedChange={setMentioned}
            placeholder={
              replyTo ? "Your reply… use @ to tag somebody" : "Add a comment… use @ to tag somebody"
            }
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-xs text-ink-500">
              {mentionsStillIn(body, mentioned).length > 0
                ? `${namesStillIn(body, mentioned).join(", ")} will be told about this.`
                : "Type @ to tag somebody and they will be told."}
            </p>
            <Button
              size="sm"
              icon={<Send className="h-3.5 w-3.5" />}
              loading={saving}
              disabled={body.trim() === ""}
              onClick={() => void post()}
            >
              {replyTo ? "Reply" : "Comment"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Which of the named people are still named, by name rather than by id.
 *
 * <p>For the line under the composer that says who will be told. Saying "two people" would make
 * somebody count back through their own sentence to find out who.
 */
function namesStillIn(text: string, mentioned: Record<string, string>): string[] {
  return Object.values(mentioned).filter((name) => text.includes(`@${name}`));
}

/**
 * A comment with the people it names picked out.
 *
 * <p>Matched against the names the server resolved rather than anything that looks like an `@`.
 * An email address in a comment is not a mention, and neither is somebody writing `@here`.
 *
 * <p>Longest name first, so "@Anita Rao Menon" is not matched as "@Anita Rao" with a stray word
 * left over.
 */
function WithMentions({ body, names }: { body: string | null; names?: string[] }) {
  if (!body) {
    return null;
  }
  const mentions = (names ?? []).filter(Boolean);
  if (mentions.length === 0) {
    return <>{body}</>;
  }

  const pattern = new RegExp(
    `(@(?:${[...mentions].sort((a, b) => b.length - a.length).map(escapeForRegExp).join("|")}))`,
    "g",
  );

  return (
    <>
      {body.split(pattern).map((piece, index) =>
        mentions.some((name) => piece === `@${name}`) ? (
          <span
            key={index}
            className="rounded bg-brand-50 px-1 font-medium text-brand-700"
          >
            {piece}
          </span>
        ) : (
          <span key={index}>{piece}</span>
        ),
      )}
    </>
  );
}

/** A name is somebody's to spell, and some of them contain regular-expression punctuation. */
function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
